import { noop } from 'lodash';
import React, { useContext, useEffect } from 'react';

import { MetricsContext } from '../Metrics';
import { INTERCOM_EVENTS } from './IntercomContext.constants';
import { mapMetricsFromApiToVm } from './IntercomContext.mapper';

interface Context {
  shutdown: () => void;
  sendMessage: (message: string) => void;
}

export const IntercomContext = React.createContext<Context>({
  shutdown: noop,
  sendMessage: noop,
});

export interface IIntercomProviderProps {
  children?: React.ReactNode;
}

export const IntercomProvider: React.FC<IIntercomProviderProps> = ({
  children,
}) => {
  const metrics = useContext(MetricsContext);

  const initializeIntercom = React.useCallback(() => {
    const data = metrics?.userId ? mapMetricsFromApiToVm(metrics) : undefined;
    const appId = process.env.NEXT_PUBLIC_INTERCOM_APP_ID ?? '';

    if (data) {
      window.Intercom?.(INTERCOM_EVENTS.boot, { app_id: appId, ...data });
      return;
    }
  }, [metrics?.userId]);

  useEffect(() => {
    initializeIntercom();
  }, [initializeIntercom]);

  const shutdown = () => {
    if (window.Intercom) {
      window.Intercom(INTERCOM_EVENTS.shutdown);
    }
  };

  const sendMessage = (message: string) => {
    if (window.Intercom) {
      window.Intercom('showNewMessage', message);
    }
  };

  return (
    <IntercomContext.Provider
      value={{
        shutdown,
        sendMessage,
      }}
    >
      {children}
    </IntercomContext.Provider>
  );
};
